import { CSSProperties, ReactNode, useRef } from "react";
import { useExpandedItem } from "@/ui-components/expanded-items";
import clsx from "clsx";
import { NewsFeedItem, getDisplayTime } from "@/shared/api/firehose/models";
import { formatTo24HourTime } from "@/utils/date-utils";
import { truncateString, decodeHtmlEntities } from "@/utils/text-utils";
import { useHasInternalNewsquawkUserRole } from "@/hooks/useHasInternalNewsquawkUserRole";
// @ts-ignore
import { useFontStore } from "@/stores/store";
import { BgOverlay } from "./BgOverlay";
import { FirehoseItemDetails } from "./FirehoseItemDetails";
import { hasTickers, normalizeTickers } from "../utils";
import { HighlightInfo } from "@/utils/portfolioUtils";
import { trackContentView, trackContentEngagement } from "@/utils/gtm";

const TRANSITION_DURATION = 5000;

export function FirehoseItem(props: {
  item: NewsFeedItem;
  titleStyle?: CSSProperties;
  endBadge?: ReactNode;
  highlightInfo?: HighlightInfo;
  renderDetails?: (item: NewsFeedItem, highlightInfo?: HighlightInfo) => ReactNode;
}) {
  const item = props.item;

  const isInternalUser = useHasInternalNewsquawkUserRole();

  const isTruthSocialStory = item.source === "truth-social";

  const toggleFirehoseStoryClicked = useFontStore(
    // @ts-ignore
    (state) => state.toggleFirehoseStoryClicked
  );

  const isClicked = useFontStore(
    // @ts-ignore
    (state) => state.clickedFirehoseStories[item.id] ?? false
  );

  const [showDetails, toggleShowDetails] = useExpandedItem(
    item.id,
    item.show_deatils ?? false
  );

  // Track when details were opened for engagement time measurement
  const openedAtRef = useRef<number | null>(null);

  const toggleDetails = () => {
    if (!isClicked) toggleFirehoseStoryClicked(item.id);

    if (!showDetails) {
      // Opening details — track content view and record open time
      openedAtRef.current = Date.now();
      const tickers = hasTickers(item) ? normalizeTickers(item.tickers).join(", ") : undefined;
      trackContentView({
        content_id: item.id,
        content_title: item.title,
        content_type: "firehose_story",
        content_category: item.backend_source ?? "unknown",
        content_source: item.source_attributable || item.source,
        content_tickers: tickers,
        content_widget: "firehose",
      });
    } else if (openedAtRef.current) {
      // Closing details — track engagement time
      trackContentEngagement({
        content_id: item.id,
        content_title: item.title,
        content_type: "firehose_story",
        content_widget: "firehose",
        engagement_time_ms: Date.now() - openedAtRef.current,
      });
      openedAtRef.current = null;
    }

    // Expansion state is per widget instance (see useExpandedItem) — never
    // written onto the item object, which every instance shares.
    toggleShowDetails();
  };

  return (
    <div className={clsx(
      "border-b border-b-gray-200 dark:border-b-gray-800 transition-colors",
      !showDetails && "bg-white dark:bg-gray-900"
    )}>
      <button
        onClick={toggleDetails}
        className={clsx(
          "focus:outline-none cursor-pointer flex items-start text-xs gap-2 w-full rounded-none transition relative bg-transparent z-0 px-2",
          isClicked && "font-normal text-gray-500 dark:text-gray-400",
          !isClicked && "font-semibold text-gray-900 dark:text-gray-100",
          showDetails && "border-x border-t border-squawk-blue/40"
        )}
      >
        {/* Left border accent for new stories (30s hold + 5s fade) */}
        {item.received_at && (
          <BgOverlay
            key={`firehose-item-${item.id}-bar`}
            startTime={new Date(item.received_at)}
            color={isTruthSocialStory ? "#B71C1C" : "#00aeef"}
          />
        )}

        {/* Brief full-row flash on arrival (3s fade) */}
        {item.received_at && (Date.now() - item.received_at < 3000) && (
          <div
            key={`flash-${item.id}-${item.received_at}`}
            className="absolute inset-0 z-10 pointer-events-none animate-flash-out"
            style={{ animationDuration: "3s", "--flash": "rgba(0, 174, 239, 0.4)" } as CSSProperties}
          />
        )}

        <span className="whitespace-nowrap text-xs text-clip">
          {formatTo24HourTime(getDisplayTime(item))}
        </span>

        <p className={"flex-1 px-1 min-w-0 text-start text-xs break-words"}>
          <span style={props.titleStyle}>
            {isInternalUser && (
              <span className="capitalize">
                {`${item.source_attributable
                  ?.replace(/\bvia .*/i, "")
                  .trim()}: `}
              </span>
            )}
            {hasTickers(item) && normalizeTickers(item?.tickers).length > 0 && (
              <span>({normalizeTickers(item?.tickers).join(", ")}) </span>
            )}

            {truncateString(decodeHtmlEntities(item.title), 280)}
          </span>
          {props.endBadge}
        </p>
        <span className="p-0.5 text-sm">{showDetails ? "↑" : "↓"}</span>
      </button>
      {showDetails && (props.renderDetails
        ? props.renderDetails(item, props.highlightInfo)
        : <FirehoseItemDetails {...item} highlightInfo={props.highlightInfo} />
      )}
    </div>
  );
}
